import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Label,
  TextInput,
  Button,
  Table,
  Badge,
  Modal,
  Spinner,
  Checkbox,
  useThemeMode
} from "flowbite-react";
import { HiSearch, HiDownload } from "react-icons/hi";
import {
  useLazyGetKBIssueInsightsQuery,
  useDownloadKBIssueInsightsReportMutation,
  useDownloadKBUnmatchedQueriesReportMutation
} from "../../api/reportsApi/reports";
import { KBIssueStat, KBUnmatchedVerdict, KBUnmatchedQuery } from "../../types/reports";
import { getErrorMessage } from "../../utils/requestUtils";
import { useReportCompanyParam } from "../../hooks/useReportCompanyParam";
import { toast } from "@/lib/toast";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import Pagination from "../../components/Pagination";
import DateRangePresets from "../../components/reports/DateRangePresets";

const truncate = (text: string, max = 50) =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;

const formatScore = (score: number | null) => (score != null ? score.toFixed(4) : "—");

const IdBadgeList: FC<{ ids: number[] }> = ({ ids }) => (
  <div className="flex flex-wrap gap-1 mt-1">
    {ids.map((id) => (
      <Badge key={id} color="gray">
        {id}
      </Badge>
    ))}
  </div>
);

const CHART_TOP_N = 10;
const TABLE_PAGE_SIZE = 10;
const ALL_UNMATCHED_VERDICTS: KBUnmatchedVerdict[] = ["PARTIAL", "NO_MATCH", "OUT_OF_SCOPE"];

const KBIssueInsightsReport: FC = () => {
  const { t } = useTranslation(["reports"]);
  const { mode } = useThemeMode();
  const isDark = mode === "dark";

  const today = new Date().toISOString().split("T")[0];

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedIssue, setSelectedIssue] = useState<{ issue: KBIssueStat; rank: number } | null>(null);
  const [selectedUnmatched, setSelectedUnmatched] = useState<KBUnmatchedQuery | null>(null);
  const [includeUnmatchedInPdf, setIncludeUnmatchedInPdf] = useState<boolean>(false);
  const [unmatchedPage, setUnmatchedPage] = useState<number>(1);
  const [verdictFilter, setVerdictFilter] = useState<Set<KBUnmatchedVerdict>>(
    () => new Set(ALL_UNMATCHED_VERDICTS)
  );

  const [triggerReport, { data: reportData, isLoading }] = useLazyGetKBIssueInsightsQuery();
  const [downloadReport, { isLoading: isDownloadLoading }] = useDownloadKBIssueInsightsReportMutation();
  const [downloadUnmatchedReport, { isLoading: isUnmatchedDownloadLoading }] = useDownloadKBUnmatchedQueriesReportMutation();
  const companyParam = useReportCompanyParam();

  const toggleVerdictFilter = (verdict: KBUnmatchedVerdict) => {
    setVerdictFilter((prev) => {
      const next = new Set(prev);
      if (next.has(verdict)) next.delete(verdict);
      else next.add(verdict);
      return next;
    });
    setUnmatchedPage(1);
  };

  const handleSearch = async () => {
    if (!startDate || !endDate) {
      toast.warn(t("selectDateWarning"));
      return;
    }

    if (startDate > endDate) {
      toast.error(t("startDateHigherThanEndDate"));
      return;
    }

    try {
      await triggerReport({ start_date: startDate, end_date: endDate, ...companyParam }).unwrap();
      setCurrentPage(1);
    } catch (error: any) {
      toast.error(getErrorMessage(error));
    }
  };

  const saveBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();

    link.remove();
    // Revoking too early can cancel the download in some browsers if it's
    // still being handed off to the OS — give it a moment first.
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleDownload = async () => {
    if (!startDate || !endDate) {
      toast.warn(t("selectDateWarning"));
      return;
    }

    try {
      toast.info(t("downloadingPdf"));

      const blob = await downloadReport({ start_date: startDate, end_date: endDate, ...companyParam }).unwrap();
      saveBlob(blob, `kb-issue-insights-${startDate}-${endDate}.pdf`);

      if (includeUnmatchedInPdf) {
        const unmatchedBlob = await downloadUnmatchedReport({ start_date: startDate, end_date: endDate, ...companyParam }).unwrap();
        // Chromium browsers silently block a second automatic download that
        // fires immediately after the first one from the same click — a
        // short gap avoids tripping that "multiple downloads" guard.
        await wait(500);
        saveBlob(unmatchedBlob, `kb-unmatched-queries-${startDate}-${endDate}.pdf`);
      }

      toast.success(t("downloadComplete"));
    } catch (error: any) {
      toast.error(t("downloadFailed", { error: getErrorMessage(error) }));
    }
  };

  const summaryCards = [
    { label: t("kbIssueInsights.summary.totalSessions"), value: reportData?.overview.total_sessions ?? 0, color: "text-blue-600" },
    { label: t("kbIssueInsights.summary.totalTurns"), value: reportData?.overview.total_turns ?? 0, color: "text-purple-600" },
    { label: t("kbIssueInsights.summary.distinctIssues"), value: reportData?.overview.total_distinct_issues ?? 0, color: "text-blue-600" },
    { label: t("kbIssueInsights.summary.totalUnmatchedQueries"), value: reportData?.unmatched_queries.length ?? 0, color: "text-purple-600" },
  ];

  const topIssues = (reportData?.top_issues ?? []).slice(0, CHART_TOP_N);
  const leastIssues = (reportData?.least_issues ?? []).slice(0, CHART_TOP_N);

  const allIssues = reportData?.issues ?? [];
  const totalPages = Math.max(1, Math.ceil(allIssues.length / TABLE_PAGE_SIZE));
  const pagedIssues = allIssues.slice((currentPage - 1) * TABLE_PAGE_SIZE, currentPage * TABLE_PAGE_SIZE);

  const filteredUnmatched = (reportData?.unmatched_queries ?? []).filter((q) => verdictFilter.has(q.gate_verdict));
  const unmatchedTotalPages = Math.max(1, Math.ceil(filteredUnmatched.length / TABLE_PAGE_SIZE));
  const pagedUnmatched = filteredUnmatched.slice(
    (unmatchedPage - 1) * TABLE_PAGE_SIZE,
    unmatchedPage * TABLE_PAGE_SIZE
  );

  const verdictBadgeColor = (verdict: KBUnmatchedVerdict) =>
    verdict === "PARTIAL" ? "warning" : verdict === "NO_MATCH" ? "failure" : "indigo";

  const barChartOptionsFor = (issues: KBIssueStat[], colors: string[]): ApexOptions => ({
    chart: {
      type: "bar",
      background: "transparent",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, distributed: true },
    },
    xaxis: {
      categories: issues.map((i) => truncate(i.issue_text, 40)),
      labels: { style: { colors: isDark ? "#fff" : "#000" } },
    },
    yaxis: {
      labels: { style: { colors: isDark ? "#fff" : "#000" } },
    },
    colors,
    dataLabels: { enabled: true },
    grid: { borderColor: isDark ? "#374151" : "#e5e7eb" },
    theme: { mode: isDark ? "dark" : "light" },
    legend: { show: false },
  });

  return (
    <div className="w-full">
      <div className="px-4 pt-6">
        <Card className="mb-6">
          <DateRangePresets
            className="mb-4"
            startDate={startDate}
            endDate={endDate}
            onChange={(s, e) => { setStartDate(s); setEndDate(e); }}
          />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <div className="mb-2 block">
                <Label htmlFor="startDate" value={t("startDate")} />
              </div>
              <TextInput
                id="startDate"
                type="date"
                value={startDate}
                max={today}
                onChange={(e) => setStartDate(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Tab") e.preventDefault();
                }}
                required
              />
            </div>
            <div>
              <div className="mb-2 block">
                <Label htmlFor="endDate" value={t("endDate")} />
              </div>
              <TextInput
                id="endDate"
                type="date"
                value={endDate}
                max={today}
                onChange={(e) => setEndDate(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Tab") e.preventDefault();
                }}
                required
              />
            </div>
            <Button color="primary" onClick={handleSearch} disabled={isLoading} isProcessing={isLoading}>
              <HiSearch className="mr-2 h-5 w-5" />
              {t("generateReport")}
            </Button>
            <Button
              color="light"
              onClick={handleDownload}
              disabled={!reportData || isDownloadLoading || isUnmatchedDownloadLoading}
            >
              {(isDownloadLoading || isUnmatchedDownloadLoading) ? (
                <Spinner size="sm" className="mr-2" />
              ) : (
                <HiDownload className="mr-2 h-5 w-5" />
              )}
              {includeUnmatchedInPdf ? t("downloadPdfs") : t("downloadPdf")}
            </Button>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <Checkbox
              id="includeUnmatchedInPdf"
              checked={includeUnmatchedInPdf}
              onChange={(e) => setIncludeUnmatchedInPdf(e.target.checked)}
            />
            <Label htmlFor="includeUnmatchedInPdf" value={t("kbIssueInsights.includeUnmatchedInPdf")} />
          </div>
        </Card>

        {reportData && (
          <div className="grid gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {summaryCards.map((card, index) => (
                <Card key={index}>
                  <h5 className="text-sm tracking-tight text-gray-500 dark:text-gray-400">
                    {card.label}
                  </h5>
                  <p className={`text-2xl font-bold ${card.color} dark:text-white`}>
                    {card.value}
                  </p>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <h5 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                  {t("kbIssueInsights.charts.topIssues")}
                </h5>
                {topIssues.length > 0 ? (
                  <div className="h-96">
                    <ReactApexChart
                      options={barChartOptionsFor(topIssues, ["#1a56db"])}
                      series={[{ name: t("kbIssueInsights.table.count"), data: topIssues.map((i) => i.count) }]}
                      type="bar"
                      height="100%"
                    />
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">{t("noDataMessage")}</p>
                )}
              </Card>
              <Card>
                <h5 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                  {t("kbIssueInsights.charts.leastIssues")}
                </h5>
                {leastIssues.length > 0 ? (
                  <div className="h-96">
                    <ReactApexChart
                      options={barChartOptionsFor(leastIssues, ["#fdba8c"])}
                      series={[{ name: t("kbIssueInsights.table.count"), data: leastIssues.map((i) => i.count) }]}
                      type="bar"
                      height="100%"
                    />
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">{t("noDataMessage")}</p>
                )}
              </Card>
            </div>

            <Card>
              <h5 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                {t("kbIssueInsights.table.title")}
              </h5>
              <div className="overflow-x-auto">
                <Table>
                  <Table.Head>
                    <Table.HeadCell>{t("kbIssueInsights.table.rank")}</Table.HeadCell>
                    <Table.HeadCell>{t("kbIssueInsights.table.issue")}</Table.HeadCell>
                    <Table.HeadCell>{t("kbIssueInsights.table.count")}</Table.HeadCell>
                    <Table.HeadCell>{t("kbIssueInsights.table.source")}</Table.HeadCell>
                    <Table.HeadCell>{t("kbIssueInsights.table.avgScore")}</Table.HeadCell>
                    <Table.HeadCell>{t("kbIssueInsights.table.lastSeen")}</Table.HeadCell>
                  </Table.Head>
                  <Table.Body className="divide-y">
                    {pagedIssues.map((issue, index) => {
                      const rank = (currentPage - 1) * TABLE_PAGE_SIZE + index + 1;
                      return (
                        <Table.Row
                          key={index}
                          onClick={() => setSelectedIssue({ issue, rank })}
                          className="cursor-pointer bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                        >
                          <Table.Cell className="font-medium text-gray-900 dark:text-white">{rank}</Table.Cell>
                          <Table.Cell className="max-w-md">
                            <div className="font-medium text-gray-900 dark:text-white">{issue.issue_text}</div>
                            {issue.solution_text && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {truncate(issue.solution_text, 120)}
                              </div>
                            )}
                          </Table.Cell>
                          <Table.Cell>{issue.count}</Table.Cell>
                          <Table.Cell>
                            <div className="flex gap-1">
                              {issue.rag_count > 0 && <Badge color="indigo">{t("kbIssueInsights.table.rag")}: {issue.rag_count}</Badge>}
                              {issue.cache_count > 0 && <Badge color="purple">{t("kbIssueInsights.table.cache")}: {issue.cache_count}</Badge>}
                            </div>
                          </Table.Cell>
                          <Table.Cell>{issue.avg_score}</Table.Cell>
                          <Table.Cell>{new Date(issue.last_seen).toLocaleDateString()}</Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
              </div>
              {allIssues.length > TABLE_PAGE_SIZE && (
                <div className="mt-4">
                  <Pagination
                    pagination={{
                      pageSize: TABLE_PAGE_SIZE,
                      pageIndex: currentPage,
                      totalPages,
                      totalItems: allIssues.length,
                    }}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </Card>

            <Card className="mb-10">
              <h5 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {t("kbIssueInsights.unmatchedQueries.title")}
              </h5>
              <div className="flex flex-wrap gap-2 mb-4">
                {ALL_UNMATCHED_VERDICTS.map((verdict) => {
                  const active = verdictFilter.has(verdict);
                  return (
                    <Badge
                      key={verdict}
                      color={active ? verdictBadgeColor(verdict) : "gray"}
                      className={`cursor-pointer select-none border transition-opacity ${
                        active
                          ? "border-transparent"
                          : "opacity-50 line-through border-gray-400 dark:border-gray-500"
                      }`}
                      onClick={() => toggleVerdictFilter(verdict)}
                    >
                      {active ? "✓ " : ""}
                      {t(`kbIssueInsights.unmatchedQueries.verdict.${verdict}`)}
                    </Badge>
                  );
                })}
              </div>
              {pagedUnmatched.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <Table.Head>
                        <Table.HeadCell>{t("kbIssueInsights.unmatchedQueries.table.query")}</Table.HeadCell>
                        <Table.HeadCell>{t("kbIssueInsights.table.verdicts")}</Table.HeadCell>
                        <Table.HeadCell>{t("kbIssueInsights.unmatchedQueries.table.candidate")}</Table.HeadCell>
                        <Table.HeadCell>{t("kbIssueInsights.unmatchedQueries.table.score")}</Table.HeadCell>
                        <Table.HeadCell>{t("kbIssueInsights.unmatchedQueries.table.time")}</Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {pagedUnmatched.map((q, index) => (
                          <Table.Row
                            key={index}
                            onClick={() => setSelectedUnmatched(q)}
                            className="cursor-pointer bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                          >
                            <Table.Cell className="max-w-md font-medium text-gray-900 dark:text-white">
                              {q.user_query}
                            </Table.Cell>
                            <Table.Cell>
                              <Badge color={verdictBadgeColor(q.gate_verdict)}>
                                {t(`kbIssueInsights.unmatchedQueries.verdict.${q.gate_verdict}`)}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell className="max-w-md">
                              {q.candidate_issue_text ?? (
                                <span className="text-gray-400 dark:text-gray-500">
                                  {t("kbIssueInsights.unmatchedQueries.noCandidate")}
                                </span>
                              )}
                            </Table.Cell>
                            <Table.Cell>{formatScore(q.candidate_score)}</Table.Cell>
                            <Table.Cell>{new Date(q.timestamp).toLocaleString()}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                  {filteredUnmatched.length > TABLE_PAGE_SIZE && (
                    <div className="mt-4">
                      <Pagination
                        pagination={{
                          pageSize: TABLE_PAGE_SIZE,
                          pageIndex: unmatchedPage,
                          totalPages: unmatchedTotalPages,
                          totalItems: filteredUnmatched.length,
                        }}
                        onPageChange={setUnmatchedPage}
                      />
                    </div>
                  )}
                </>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">{t("noDataMessage")}</p>
              )}
            </Card>
          </div>
        )}

        <Modal show={selectedIssue !== null} onClose={() => setSelectedIssue(null)} size="xl">
          <Modal.Header>{t("kbIssueInsights.modal.title")}</Modal.Header>
          <Modal.Body>
            {selectedIssue && (
              <div className="space-y-4">
                <div>
                  <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                    {t("kbIssueInsights.table.issue")}
                  </h6>
                  <p className="text-gray-900 dark:text-white">{selectedIssue.issue.issue_text}</p>
                </div>
                {selectedIssue.issue.solution_text && (
                  <div>
                    <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {t("kbIssueInsights.modal.solution")}
                    </h6>
                    <p className="text-gray-900 dark:text-white whitespace-pre-line">{selectedIssue.issue.solution_text}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {t("kbIssueInsights.table.rank")}
                    </h6>
                    <p className="text-gray-900 dark:text-white">#{selectedIssue.rank}</p>
                  </div>
                  <div>
                    <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {t("kbIssueInsights.table.count")}
                    </h6>
                    <p className="text-gray-900 dark:text-white">{selectedIssue.issue.count}</p>
                  </div>
                  <div>
                    <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {t("kbIssueInsights.modal.distinctSessions")}
                    </h6>
                    <p className="text-gray-900 dark:text-white">{selectedIssue.issue.distinct_sessions}</p>
                  </div>
                  <div>
                    <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {t("kbIssueInsights.table.avgScore")}
                    </h6>
                    <p className="text-gray-900 dark:text-white">{selectedIssue.issue.avg_score}</p>
                  </div>
                  <div>
                    <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {t("kbIssueInsights.table.source")}
                    </h6>
                    <div className="flex gap-1 mt-1">
                      {selectedIssue.issue.rag_count > 0 && <Badge color="indigo">{t("kbIssueInsights.table.rag")}: {selectedIssue.issue.rag_count}</Badge>}
                      {selectedIssue.issue.cache_count > 0 && <Badge color="purple">{t("kbIssueInsights.table.cache")}: {selectedIssue.issue.cache_count}</Badge>}
                    </div>
                  </div>
                  {selectedIssue.issue.db_kb_issue_ids.length > 0 && (
                    <div>
                      <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                        {t("kbIssueInsights.modal.kbIssueId")}
                      </h6>
                      <IdBadgeList ids={selectedIssue.issue.db_kb_issue_ids} />
                    </div>
                  )}
                  {selectedIssue.issue.db_kb_solution_ids.length > 0 && (
                    <div>
                      <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                        {t("kbIssueInsights.modal.solutionId")}
                      </h6>
                      <IdBadgeList ids={selectedIssue.issue.db_kb_solution_ids} />
                    </div>
                  )}
                  {selectedIssue.issue.db_ticket_ids.length > 0 && (
                    <div>
                      <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                        {t("kbIssueInsights.modal.ticketId")}
                      </h6>
                      <IdBadgeList ids={selectedIssue.issue.db_ticket_ids} />
                    </div>
                  )}
                  <div>
                    <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {t("kbIssueInsights.modal.firstSeen")}
                    </h6>
                    <p className="text-gray-900 dark:text-white">{new Date(selectedIssue.issue.first_seen).toLocaleString()}</p>
                  </div>
                  <div>
                    <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {t("kbIssueInsights.table.lastSeen")}
                    </h6>
                    <p className="text-gray-900 dark:text-white">{new Date(selectedIssue.issue.last_seen).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
        </Modal>

        <Modal show={selectedUnmatched !== null} onClose={() => setSelectedUnmatched(null)} size="xl">
          <Modal.Header>{t("kbIssueInsights.unmatchedQueries.modal.title")}</Modal.Header>
          <Modal.Body>
            {selectedUnmatched && (
              <div className="space-y-4">
                <div>
                  <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                    {t("kbIssueInsights.unmatchedQueries.table.query")}
                  </h6>
                  <p className="text-gray-900 dark:text-white">{selectedUnmatched.user_query}</p>
                </div>
                <div>
                  <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                    {t("kbIssueInsights.table.verdicts")}
                  </h6>
                  <Badge color={verdictBadgeColor(selectedUnmatched.gate_verdict)} className="w-fit mt-1">
                    {t(`kbIssueInsights.unmatchedQueries.verdict.${selectedUnmatched.gate_verdict}`)}
                  </Badge>
                </div>
                {selectedUnmatched.candidate_issue_text && (
                  <div>
                    <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {t("kbIssueInsights.unmatchedQueries.table.candidate")}
                    </h6>
                    <p className="text-gray-900 dark:text-white">{selectedUnmatched.candidate_issue_text}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {t("kbIssueInsights.unmatchedQueries.table.score")}
                    </h6>
                    <p className="text-gray-900 dark:text-white">{formatScore(selectedUnmatched.candidate_score)}</p>
                  </div>
                  {selectedUnmatched.candidate_issue_ids.length > 0 && (
                    <div>
                      <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                        {t("kbIssueInsights.modal.kbIssueId")}
                      </h6>
                      <IdBadgeList ids={selectedUnmatched.candidate_issue_ids} />
                    </div>
                  )}
                  {selectedUnmatched.candidate_solution_ids.length > 0 && (
                    <div>
                      <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                        {t("kbIssueInsights.modal.solutionId")}
                      </h6>
                      <IdBadgeList ids={selectedUnmatched.candidate_solution_ids} />
                    </div>
                  )}
                  {selectedUnmatched.candidate_ticket_ids.length > 0 && (
                    <div>
                      <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                        {t("kbIssueInsights.modal.ticketId")}
                      </h6>
                      <IdBadgeList ids={selectedUnmatched.candidate_ticket_ids} />
                    </div>
                  )}
                  <div>
                    <h6 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {t("kbIssueInsights.unmatchedQueries.table.time")}
                    </h6>
                    <p className="text-gray-900 dark:text-white">{new Date(selectedUnmatched.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default KBIssueInsightsReport;
